import type { IHeaderAngularComp } from '@ag-grid-community/angular';
import type { IHeaderParams, RowNode } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Optional } from '@angular/core';
import { SsrmSelectAllPromptService } from './ssrm-select-all-prompt.service';

/**
 * Minimal header checkbox for the SSRM grid's row-checkbox column. AG-Grid
 * 28's built-in `headerCheckboxSelection: true` doesn't render under SSRM
 * mode (the property is documented but AG-Grid's SSRM rendering path
 * skips it — the header cell stays empty). This component fills that
 * gap with a "select all rows on the current page" checkbox.
 *
 * Behavior mirrors AG-Grid's clientSide `headerCheckboxSelection`:
 *   - unchecked when no visible rows are selected
 *   - indeterminate when some visible rows are selected
 *   - checked when all visible rows are selected
 *   - click: if currently checked or indeterminate → deselect visible;
 *            else → select visible
 *
 * "Visible" here means rows on the CURRENT PAGE (computed via
 * `paginationGetCurrentPage` + `paginationGetPageSize`), NOT all rows in
 * AG-Grid's loaded blocks. With our default 1-block-per-page setup
 * these are equivalent, but a configuration with `pageSize <
 * cacheBlockSize` would have multiple pages per block — we pick the
 * page-level interpretation for consistency with the legacy grid's UX.
 *
 * Selecting via `setSelected(value, false, suppressFinishActions=true)`
 * for all rows EXCEPT the last; the last call fires AG-Grid's
 * `selectionChanged` event ONCE so the SSRM grid's onSelectionChanged
 * handler computes the new full cross-page set in a single round-trip
 * (rather than N separate state updates for N visible rows).
 *
 * Note: this is "select all on page" only. True "select all matching"
 * (across pages) is handled by the SSRM grid's `selectAllMatching()`
 * imperative method, which gets a button-trigger UI in the bulk-action work
 * bulk-action toolbar.
 */
@Component({
  selector: 'lib-ssrm-header-checkbox',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="ag-checkbox ag-input-field ag-labeled ag-label-align-right"
      role="presentation"
      (click)="onClick($event)"
    >
      <div
        class="ag-input-field-label ag-label ag-hidden ag-checkbox-label"
        role="presentation"
      ></div>
      <div
        class="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper"
        role="presentation"
        [class.ag-checked]="checked && !indeterminate"
        [class.ag-indeterminate]="indeterminate"
        tabindex="0"
      >
        <input
          type="checkbox"
          class="ag-input-field-input ag-checkbox-input"
          [checked]="checked"
          [indeterminate]="indeterminate"
          aria-label="Select all rows on this page"
          tabindex="-1"
        />
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
      }
      .ag-checkbox {
        cursor: pointer;
      }
    `,
  ],
})
export class SsrmHeaderCheckboxComponent implements IHeaderAngularComp {
  protected checked = false;
  protected indeterminate = false;
  private params!: IHeaderParams;

  /**
   * Optional injection of the SSRM grid's per-instance prompt service.
   * Marked @Optional so this header component can also be used by
   * standalone test harnesses or grids that don't provide the service
   * (e.g., consumers who set `[showSelectAllBanner]="false"` on the grid
   * — the service is still provided per-grid-instance by
   * CdwSsrmGridComponent, so in practice this is always available within
   * the grid; the @Optional is defensive insurance against unexpected
   * provider misconfig).
   */
  constructor(
    private readonly cdr: ChangeDetectorRef,
    @Optional() private readonly promptService: SsrmSelectAllPromptService | null
  ) {}

  public agInit(params: IHeaderParams): void {
    this.params = params;
    // Re-evaluate state on any selection or model change. AG-Grid's
    // `selectionChanged` fires for both user clicks and our SSRM grid's
    // mirror calls, so paginating into a page where rows are already
    // selected via cross-page state correctly updates the header to
    // show indeterminate/checked.
    //
    // The three listeners below are removed in `destroy()` (Brian's PR
    // #885 review #2 — without symmetric `removeEventListener`, each
    // column-reorder cycle would leak a listener). `this.refresh` is an
    // arrow function (stable reference) so the remove call below matches.
    this.params.api.addEventListener('selectionChanged', this.refresh);
    this.params.api.addEventListener('modelUpdated', this.refresh);
    this.params.api.addEventListener('paginationChanged', this.refresh);
    this.refresh();
  }

  /**
   * AG-Grid lifecycle teardown for header components. Called when the
   * column is reordered, hidden, or the grid is destroyed. Symmetric
   * cleanup of the three event listeners installed in `agInit`. Without
   * this, every column reorder would leak three listeners — the bug
   * surfaced in Brian Zengel's review of PR #885 (review item #2).
   *
   * `this.refresh` is the SAME arrow-function reference passed to
   * `addEventListener` (defined as a class field, not a method) so the
   * remove call here matches.
   */
  public destroy(): void {
    if (!this.params?.api) return;
    this.params.api.removeEventListener('selectionChanged', this.refresh);
    this.params.api.removeEventListener('modelUpdated', this.refresh);
    this.params.api.removeEventListener('paginationChanged', this.refresh);
  }

  /**
   * Recompute `checked` / `indeterminate` from current visible-row state.
   * Called both as the AG-Grid header lifecycle `refresh()` and as the
   * handler for our three event listeners (selectionChanged,
   * modelUpdated, paginationChanged).
   *
   * `cdr.detectChanges()` is required because AG-Grid's `addEventListener`
   * is an internal pub/sub — not a Zone.js-patched DOM listener — so when
   * this fires from a listener path, NgZone never notices the field
   * mutations and the template's `[indeterminate]` / `.ag-indeterminate`
   * bindings stay frozen. The visible bug: clicking the dash header to
   * deselect-all-on-page correctly drops `selectedCount` to 0, but the
   * dash icon never repaints because no CD cycle fires for this header
   * cell. The AG-Grid lifecycle path (the one `mirrorIsSelectedFromData`
   * triggers via `refreshHeader()`) already runs in zone, so the explicit
   * detect is redundant-but-safe there.
   */
  public refresh = (): boolean => {
    const visible = this.getVisibleRowNodes();
    const selectedCount = visible.filter((n) => n.isSelected()).length;
    this.checked = selectedCount > 0 && selectedCount === visible.length;
    this.indeterminate =
      selectedCount > 0 && selectedCount < visible.length;
    this.cdr.detectChanges();
    return true;
  };

  protected onClick(event: MouseEvent): void {
    event.stopPropagation();
    // Prevent the browser from toggling the input element's `checked`
    // state directly — we drive `checked`/`indeterminate` from the
    // derived state of visible row selections via `refresh()`. Without
    // preventDefault the input briefly desyncs when the user clicks the
    // input area directly (vs. clicking the wrapper graphic).
    event.preventDefault();
    const visible = this.getVisibleRowNodes();
    if (visible.length === 0) return;
    const targetState = !(this.checked || this.indeterminate);
    // Filter to rows whose state actually changes. Critical: AG-Grid's
    // `setSelected` is idempotent and does NOT fire `selectionChanged`
    // when the requested value matches the current state, even with
    // `suppressFinishActions=false`. Iterating all visible rows and
    // relying on the last call to fire the batch event silently breaks
    // when that last row happens to already be in the target state —
    // e.g., going dash→unchecked from a page with only the first two
    // rows selected: rows 1-2 flip and re-render, but row N (last in
    // DOM order, already unchecked) is a no-op, so `selectionChanged`
    // never fires → header `refresh()` never runs (stays in dash) and
    // the grid's `onSelectionChanged` never emits the per-row
    // `(selectionToggled)` events the consumer needs to call the
    // backend `deselectByIds`. Filtering to changed-only guarantees the
    // last call is a real transition.
    const changing = visible.filter((n) => n.isSelected() !== targetState);
    if (changing.length === 0) return;
    for (let i = 0; i < changing.length; i++) {
      const isLast = i === changing.length - 1;
      changing[i].setSelected(
        targetState,
        /*clearSelection*/ false,
        /*suppressFinishActions*/ !isLast
      );
    }
    // banner trigger: when user just escalated to "page selected"
    // AND there are more matching rows beyond the current page, signal
    // the banner to appear via the per-grid prompt service. Deselects
    // dismiss the prompt so paginating + clearing doesn't leave a stale
    // banner.
    if (this.promptService) {
      if (targetState) {
        const totalMatching = this.params.api.paginationGetRowCount();
        if (totalMatching > visible.length) {
          this.promptService.triggerPagePrompt();
        }
      } else {
        this.promptService.dismissPrompt();
      }
    }
  }

  /**
   * Rows on the current pagination page. Uses
   * `getDisplayedRowAtIndex` rather than `forEachNode` because the
   * latter walks ALL loaded blocks, which would over-select when
   * multiple pages share a block.
   */
  private getVisibleRowNodes(): RowNode[] {
    const api = this.params.api;
    const currentPage = api.paginationGetCurrentPage();
    const pageSize = api.paginationGetPageSize();
    const totalRows = api.paginationGetRowCount();
    const start = currentPage * pageSize;
    const endExclusive = Math.min(start + pageSize, totalRows);
    const nodes: RowNode[] = [];
    for (let i = start; i < endExclusive; i++) {
      const node = api.getDisplayedRowAtIndex(i);
      if (node) nodes.push(node as RowNode);
    }
    return nodes;
  }
}
